// SPDX-License-Identifier: AGPL-3.0-or-later
// Keep a highlighted row in view by scrolling its list only. scrollIntoView also scrolls every
// clipped ancestor, so a picker opening in a phone sheet (still below the screen while it slides
// in) dragged the whole sheet up past the status bar and left a gap under it, and an approval's
// Show step (2026-10-30) shifted the whole chat panel up with blank room under the composer.
// Agents code never calls scrollIntoView (AgentScrollGuard.test.ts); it uses these.

export function revealInList(list: HTMLElement | null, row: HTMLElement | null | undefined): void {
	if (!list || !row) return;
	const box = list.getBoundingClientRect();
	const at = row.getBoundingClientRect();
	if (at.top < box.top) list.scrollTop -= box.top - at.top;
	else if (at.bottom > box.bottom) list.scrollTop += Math.min(at.bottom - box.bottom, at.top - box.top);
}

/** The nearest ancestor that scrolls up and down (overflow auto or scroll, and taller than its box). */
export function scrollParent(el: Element): HTMLElement | null {
	for (let parent = el.parentElement; parent; parent = parent.parentElement) {
		const overflow = getComputedStyle(parent).overflowY;
		if (
			(overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay') &&
			parent.scrollHeight > parent.clientHeight
		)
			return parent;
	}
	return null;
}

/**
 * Scrolls `el` to the start, middle or end of the list it sits in (its nearest scrolling ancestor,
 * or `list`), and nothing else: the clipped columns and sheets around the list stay where they are.
 */
export function scrollToInList(
	el: Element | null | undefined,
	{
		block = 'center',
		behavior = 'auto',
		list,
	}: {block?: 'start' | 'center' | 'end'; behavior?: ScrollBehavior; list?: HTMLElement | null} = {},
): void {
	if (!el) return;
	// A list given that doesn't scroll (it fits, or its parent scrolls instead): the nearest one that does.
	const box = list && list.scrollHeight > list.clientHeight ? list : scrollParent(el);
	if (!box) return;
	const at = el.getBoundingClientRect();
	const offset = at.top - box.getBoundingClientRect().top;
	const by =
		block === 'start'
			? offset
			: block === 'end'
				? offset + at.height - box.clientHeight
				: offset - (box.clientHeight - at.height) / 2;
	box.scrollBy({top: by, behavior});
}
